import React, { useCallback, useRef, useState } from "react";
import ReactQuill from "react-quill-new";
import "./editor.css";
import { markdown2html } from "../message-board/htmlToMarkdown";
import { looksLikeMarkdown } from "./looksLikeMarkdown";

/**
 * to support images we would need to:
 *  a) capture the image, upload it and then insert a reference
 *  b) only allow inserting of previously uploaded images
 */

/**
 * @param {object} props
 * @param {string} props.initialValue
 * @param {(text: string) => void} props.onChange
 * @param {boolean} [props.enableMarkdownPaste]
 */
export const HtmlEditor = ({ initialValue = "", onChange, enableMarkdownPaste = false }) => {
  const [value, setValue] = useState(initialValue);
  const quillRef = useRef(null);

  const handlePasteCapture = useCallback(
    /** @param {React.ClipboardEvent} e */ (e) => {
      if (!enableMarkdownPaste) return;

      const clipboard = e.clipboardData;
      if (!clipboard) return;

      const html = clipboard.getData("text/html");
      if (html && html.trim()) return; // HTML paste: let Quill handle it normally

      const text = clipboard.getData("text/plain");
      if (!text || !looksLikeMarkdown(text)) return;

      e.preventDefault();

      const quill = quillRef.current?.getEditor?.();
      if (!quill) return;

      const selection = quill.getSelection(true);
      const index = selection?.index ?? quill.getLength();
      const length = selection?.length ?? 0;

      if (length > 0) {
        quill.deleteText(index, length, "user");
      }

      const convertedHtml = markdown2html(text);
      const delta = quill.clipboard.convert({ html: convertedHtml });
      const insertedLength = (delta?.ops ?? []).reduce((sum, op) => {
        if (!op || op.insert == null) return sum;
        if (typeof op.insert === "string") return sum + op.insert.length;
        return sum + 1;
      }, 0);

      quill.clipboard.dangerouslyPasteHTML(index, convertedHtml, "user");
      quill.setSelection(index + insertedLength, 0, "silent");
    },
    [enableMarkdownPaste]
  );

  return (
    <div data-text-editor="name" onPasteCapture={handlePasteCapture}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={(text) => {
          setValue(text);
          onChange(text);
        }}
        bounds={`[data-text-editor="name"]`}
        preserveWhitespace
        readOnly={false}
        modules={{
          toolbar: [
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            ["bold", "italic", "blockquote"],
            [{ list: "ordered" }, { list: "bullet" }],
            // [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
            // [{ indent: "-1" }, { indent: "+1" }],
            ["link"],
            ["clean"],
          ],
          clipboard: {
            matchVisual: false,
          },
        }}
        formats={["header", "bold", "italic", "blockquote", "underline", "list", "bullet", "link"]}
      />
    </div>
  );
};
